import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, pollJob, jobErrorMessage, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Loader2, ArrowLeft, Check, X, RotateCcw, Archive, Save, Sparkles, Wand2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { CONTENT_STATUS_STYLES, CONTENT_PLATFORMS } from "./constants";

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-800 mt-1 break-words">{value || "—"}</p>
    </div>
  );
}

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [edit, setEdit] = useState({ hook: "", script: "", caption: "", cta: "", compliance_notes: "" });
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [correctionsOpen, setCorrectionsOpen] = useState(false);
  const [variationOpen, setVariationOpen] = useState(false);
  const [variationPlatform, setVariationPlatform] = useState("Instagram");

  const load = () =>
    api.get(`/marketing/content/${id}`).then((r) => {
      setItem(r.data);
      setEdit({
        hook: r.data.hook || "", script: r.data.script || "", caption: r.data.caption || "",
        cta: r.data.cta || "", compliance_notes: r.data.compliance_notes || "",
      });
    }).catch(() => setItem(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveEdits = async () => {
    setSaving(true);
    try { await api.put(`/marketing/content/${id}`, edit); toast.success("Content saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const action = async (path, body) => {
    setBusy(true);
    try { await api.post(`/marketing/content/${id}/${path}`, body || {}); load(); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); return false; }
    finally { setBusy(false); }
  };

  const regen = async (parts) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/content/${id}/regenerate`, { parts }, idemHeaders(newIdemKey()));
      const job = await pollJob(data.job_id);
      if (job.status === "Failed" || job.status === "Cancelled") { toast.error(jobErrorMessage(job)); return; }
      toast.success("Regenerated"); load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Regeneration failed"); }
    finally { setBusy(false); }
  };

  const applyCorrections = () => setCorrectionsOpen(true);
  const doApplyCorrections = async () => {
    setBusy(true);
    try { await api.post(`/marketing/content/${id}/apply-corrections`); toast.success("Corrections applied"); setCorrectionsOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const createVariation = () => { setVariationPlatform(item?.platform || "Instagram"); setVariationOpen(true); };
  const createMedia = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/content/${id}/create-media`, {}, idemHeaders(newIdemKey()));
      const job = await pollJob(data.job_id, { timeout: 240000 });
      if (job.status === "Failed" || job.status === "Cancelled") { toast.error(jobErrorMessage(job)); }
      else { toast.success(job.result?.valid ? "Media created — see Media Review" : "Media generated but failed validation — open Media Review"); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Media creation failed"); }
    finally { setBusy(false); }
  };
  const doCreateVariation = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/content/${id}/create-variation`, { platform: variationPlatform }, idemHeaders(newIdemKey()));
      const job = await pollJob(data.job_id);
      if (job.status === "Failed" || job.status === "Cancelled") { toast.error(jobErrorMessage(job)); return; }
      toast.success("Variation created"); setVariationOpen(false); navigate("/marketing/content");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Failed"); }
    finally { setBusy(false); }
  };

  if (item === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (item === false) return <p className="text-sm text-red-600">Content not found.</p>;

  return (
    <div data-testid="content-detail">
      <button onClick={() => navigate("/marketing/content")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="content-back">
        <ArrowLeft className="h-4 w-4" /> Back to Content Queue
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">{item.title}</h2>
          <span className={`inline-flex mt-2 rounded-full px-3 py-1 text-xs font-semibold ${CONTENT_STATUS_STYLES[item.status] || "bg-gray-100 text-gray-600"}`} data-testid="content-status-badge">{item.status}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {item.status === "Archived" ? (
            <Button onClick={() => action("restore")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="restore-content-button"><RotateCcw className="h-4 w-4 mr-1.5" /> Restore</Button>
          ) : (
            <>
              <Button onClick={() => action("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-button"><Check className="h-4 w-4 mr-1.5" /> Approve</Button>
              <Button onClick={() => setRejectOpen(true)} disabled={busy} variant="outline" className="border-gray-200 hover:border-red-500 hover:text-red-600" data-testid="reject-button"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
              <Button onClick={() => action("return-to-draft")} disabled={busy} variant="outline" className="border-gray-200" data-testid="return-draft-button"><RotateCcw className="h-4 w-4 mr-1.5" /> Return to Draft</Button>
              <Button onClick={() => action("archive")} disabled={busy} variant="outline" className="border-gray-200 hover:border-amber-500 hover:text-amber-600" data-testid="archive-content-button"><Archive className="h-4 w-4 mr-1.5" /> Archive</Button>
              {item.status === "Approved" && (
                <Button onClick={createMedia} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="create-media-button"><Sparkles className="h-4 w-4 mr-1.5" /> Create Media</Button>
              )}
            </>
          )}
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 inline-block">
        Approval reviews content only — publishing & scheduling arrive in a <strong>later phase</strong>.
      </p>

      {item.status === "Rejected" && item.rejection_reason && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="rejection-reason">
          <strong>Rejected:</strong> {item.rejection_reason}
        </div>
      )}

      {/* Editable fields */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 space-y-4" data-testid="content-editable">
        <h3 className="font-heading font-bold">Review & Edit</h3>
        <div className="space-y-2"><Label>Hook</Label><Textarea value={edit.hook} onChange={(e) => setEdit((s) => ({ ...s, hook: e.target.value }))} data-testid="edit-hook" className="focus-visible:ring-[#581C87]" /></div>
        <div className="space-y-2"><Label>Main Script / Copy</Label><Textarea value={edit.script} onChange={(e) => setEdit((s) => ({ ...s, script: e.target.value }))} data-testid="edit-script" className="focus-visible:ring-[#581C87] min-h-[120px]" /></div>
        <div className="space-y-2"><Label>Caption</Label><Textarea value={edit.caption} onChange={(e) => setEdit((s) => ({ ...s, caption: e.target.value }))} data-testid="edit-caption" className="focus-visible:ring-[#581C87]" /></div>
        <div className="space-y-2"><Label>CTA</Label><Input value={edit.cta} onChange={(e) => setEdit((s) => ({ ...s, cta: e.target.value }))} data-testid="edit-cta" className="focus-visible:ring-[#581C87]" /></div>
        <div className="space-y-2"><Label>Compliance Notes</Label><Textarea value={edit.compliance_notes} onChange={(e) => setEdit((s) => ({ ...s, compliance_notes: e.target.value }))} data-testid="edit-compliance" className="focus-visible:ring-[#581C87]" /></div>
        <div className="flex justify-end">
          <Button onClick={saveEdits} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="save-content-edits">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Changes</>}
          </Button>
        </div>
      </div>

      {/* Compliance summary */}
      {item.compliance && (
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6" data-testid="compliance-panel">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#581C87]" />
            <h3 className="font-heading font-bold">Compliance & Factual Review</h3>
            <span className={`ml-2 rounded-full px-2.5 py-0.5 text-xs font-semibold ${item.compliance.result === "Blocked" ? "bg-red-100 text-red-700" : item.compliance.result === "Warning" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="compliance-result">{item.compliance.result} · {item.compliance.risk} risk</span>
            {item.confirm_fact && <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700">Confirm Fact</span>}
          </div>
          {item.compliance.reasons?.length > 0 && (
            <ul className="mt-3 text-sm text-gray-600 list-disc pl-5 space-y-1">{item.compliance.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
          )}
          {item.compliance.missing_factual_support?.length > 0 && (
            <p className="mt-2 text-sm text-blue-700">Missing support: {item.compliance.missing_factual_support.join("; ")}</p>
          )}
          {item.compliance.recommended_corrections?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Recommended corrections</p>
              <ul className="mt-1 text-sm text-gray-600 list-disc pl-5">{item.compliance.recommended_corrections.map((r, i) => <li key={i}>{r}</li>)}</ul>
              <Button onClick={applyCorrections} disabled={busy} variant="outline" size="sm" className="mt-3 border-gray-200 hover:border-[#581C87] hover:text-[#581C87]" data-testid="apply-corrections-button">Apply Compliance Corrections</Button>
            </div>
          )}
          <p className="mt-3 text-xs text-gray-500">Rules used: {(item.compliance.rules_used || []).join(", ")}</p>
        </div>
      )}

      {/* AI regeneration */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6" data-testid="ai-regenerate-panel">
        <div className="flex items-center gap-2"><Wand2 className="h-4 w-4 text-[#581C87]" /><h3 className="font-heading font-bold">AI Regeneration</h3></div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={() => regen(["all"])} disabled={busy} variant="outline" className="border-gray-200" data-testid="regen-all-button"><Sparkles className="h-4 w-4 mr-1.5" /> Regenerate Entire Item</Button>
          <Button onClick={() => regen(["hook"])} disabled={busy} variant="outline" className="border-gray-200" data-testid="regen-hook-button">Regenerate Hook</Button>
          <Button onClick={() => regen(["script"])} disabled={busy} variant="outline" className="border-gray-200" data-testid="regen-script-button">Regenerate Script</Button>
          <Button onClick={() => regen(["caption"])} disabled={busy} variant="outline" className="border-gray-200" data-testid="regen-caption-button">Regenerate Caption</Button>
          <Button onClick={() => regen(["cta"])} disabled={busy} variant="outline" className="border-gray-200" data-testid="regen-cta-button">Regenerate CTA</Button>
          <Button onClick={createVariation} disabled={busy} variant="outline" className="border-gray-200" data-testid="create-variation-button">Create New Variation</Button>
        </div>
        <p className="mt-3 text-xs text-gray-500">All regenerated content is re-checked by the compliance process.</p>
      </div>

      {/* Revision history */}
      {item.revisions?.length > 0 && (
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6" data-testid="revision-history">
          <h3 className="font-heading font-bold">Revision History ({item.revisions.length})</h3>
          <div className="mt-3 space-y-2">
            {item.revisions.map((rev, i) => (
              <div key={i} className="text-xs text-gray-500 border-l-2 border-gray-200 pl-3">
                <span className="font-mono">{(rev.at || "").slice(0, 16).replace("T", " ")}</span> · changed: {(rev.parts || []).join(", ")}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Read-only metadata */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 bg-white border border-gray-200 rounded-xl p-6">
        <Meta label="Topic" value={item.topic} />
        <Meta label="Content Pillar" value={item.content_pillar} />
        <Meta label="Content Format" value={item.content_format} />
        <Meta label="Platform" value={item.platform} />
        <Meta label="CTA Keyword" value={item.cta_keyword} />
        <Meta label="Landing-page URL" value={item.landing_page_url} />
        <Meta label="Creation Source" value={item.creation_source} />
        <Meta label="Scheduled" value={item.scheduled_at} />
        <Meta label="Published URL" value={item.published_url} />
        <Meta label="Campaign ID" value={item.campaign_id} />
        <Meta label="Offer ID" value={item.offer_id} />
        <Meta label="Content Purpose" value={item.content_purpose} />
        <Meta label="Recurring Series" value={item.recurring_series} />
        <Meta label="Hashtags" value={(item.hashtags || []).join(" ")} />
        <Meta label="On-screen Text" value={item.on_screen_text} />
        <Meta label="Visual Brief" value={item.visual_brief} />
        <Meta label="Scene Sequence" value={item.scene_sequence} />
        <Meta label="B-roll" value={item.b_roll} />
        <Meta label="Image Prompt" value={item.image_prompt} />
        <Meta label="Video Prompt" value={item.video_prompt} />
        <Meta label="First Comment" value={item.first_comment} />
        <Meta label="Engagement Question" value={item.engagement_question} />
        <Meta label="Comment Response Direction" value={item.comment_response_direction} />
        <Meta label="Factual Basis" value={item.factual_basis} />
        <Meta label="Generation Batch ID" value={item.generation_batch_id} />
        <Meta label="Last Updated" value={item.updated_at} />
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject Content</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason for rejection *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" className="focus-visible:ring-red-500" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!reason.trim()) { toast.error("A reason is required"); return; }
                const ok = await action("reject", { reason });
                if (ok) { toast.success("Content rejected"); setRejectOpen(false); setReason(""); }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-button"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={correctionsOpen} onOpenChange={setCorrectionsOpen}>
        <DialogContent className="max-w-md" data-testid="corrections-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Apply Compliance Corrections</DialogTitle>
            <DialogDescription>Flagged phrases will be removed. The original text is preserved in revision history and the item stays in review for a human read.</DialogDescription>
          </DialogHeader>
          {item.compliance?.recommended_corrections?.length > 0 && (
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1 py-2">
              {item.compliance.recommended_corrections.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCorrectionsOpen(false)}>Cancel</Button>
            <Button onClick={doApplyCorrections} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="confirm-corrections-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply Corrections"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={variationOpen} onOpenChange={setVariationOpen}>
        <DialogContent className="max-w-md" data-testid="variation-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create New Variation</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Platform</Label>
            <Select value={variationPlatform} onValueChange={setVariationPlatform}>
              <SelectTrigger data-testid="variation-platform-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVariationOpen(false)}>Cancel</Button>
            <Button onClick={doCreateVariation} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="confirm-variation-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Variation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
